import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { Button, Text } from 'ui'
import { ReplayIcon, XIcon } from './icons'
import { SortOption, ValidityFilters, FilterModalProps } from './types'
import { ModalHeader, hideScrollbarStyles, ModalButtonContainer, FlexLineWithGap, WhiteRoundedContainer, StyledCheckBox } from './SharedStyledComponents'

const Container = styled.div`
display: flex;
width: 640px;
padding: 24px 0 0 0;
flex-direction: column;
max-height: 90vh;
overflow-y: auto;
  ${hideScrollbarStyles}
align-items: flex-start;
gap: 12px;
flex-shrink: 0;
border-radius: 16px;
background: var(--Neutral-Surface-Light, #F6F6F6);
position: absolute;
bottom: 15px;
left: 16px;
z-index: 9999;
box-sizing: border-box;

@media (max-width: 1081px) {
  width: 100%;
  height: 100%;
  max-height: 100vh;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  border-radius: 0;
  padding: 16px 0 80px 0;
}
`

const ContentWrapper = styled.div`
  padding: 0 16px;
  width: 100%;
  box-sizing: border-box;
`

const ClearButtonContainer = styled.div`
display: flex;
padding-bottom: 8px;
justify-content: flex-start;
align-items: center;
gap: 10px;
flex-shrink: 0;
align-self: stretch;
box-sizing: border-box;
`

const ClearAllButton = styled.div`
display: flex;
width: 74px;
height: 24px;
padding: 16px;
justify-content: center;
align-items: center;
gap: 5px;
cursor: pointer;
`

const FiltersContainer = styled(WhiteRoundedContainer)<{ marginBottom?: number }>`
justify-content: center;
box-sizing: border-box;
margin-bottom: ${({ marginBottom }) => marginBottom ?? 16}px;
@media (max-width: 1081px) {
  margin-bottom: ${({ marginBottom }) => marginBottom ?? 16}px;
}
`

const RadioButtonContainer = styled.div`
display: flex;
justify-content: center;
align-items: center;
cursor: pointer;
`

const RadioButtonBase = styled.div<{ checked: boolean }>`
display: flex;
width: 20px;
height: 20px;
border-radius: 10px;
border: ${({ checked }) => !checked ? '2px solid var(--Neutral-Borders-Mid, #B0B0B0)' : '6px solid var(--Benefits-Surface-Main, #16AA40)'};
`

const CategoryChip = styled.div<{ padding?: string }>`
display: flex;
height: 37px;
padding: ${({ padding }) => padding || '8px 16px'};
justify-content: center;
align-items: center;
border-radius: 30px;
width: fit-content;
white-space: nowrap;
border: 1px solid var(--Neutral-Borders-Default, #D1D1D1);
  &:hover {
    background: #E9ECEF;
    border-color: #DEE2E6;
  }

  &.active {
    background: #272727;
    color: white;
    border-color: #272727;
  }
    cursor: pointer;
`

export const CheckBox = StyledCheckBox

const CandleStickContainer = styled.div`
display: flex;
width: 576px;
justify-content: space-between;
align-items: flex-end;
border-bottom: 1.852px solid var(--Benefits-Borders-Default, #16AA40);
position: relative;
flex-direction: row-reverse;
@media (max-width: 1081px) {
  width: 100%;
}
`

const CandleStick = styled.div<{ height: number }>`
width: 7.408px;
height: ${({ height }) => height}px;
flex-shrink: 0;
border-radius: 22.225px 22.225px 0 0;
background: #16AA40;
`

const PriceDisplayButton = styled.div`
display: flex;
padding-top: 16px;
justify-content: space-between;
align-items: flex-start;
align-self: stretch;
box-sizing: border-box;
`

const DisplayButton = styled.div`
display: flex;
height: 40px;
padding: 13px 16px;
flex-direction: column;
justify-content: center;
align-items: center;
gap: 10px;
border-radius: 8px;
border: 1px solid var(--Neutral-Borders-Default, #D1D1D1);
background: var(--Neutral-Surface-Minimal, #FFF);
`

const BlackDragButton = styled.div<{ left: number }>`
position: absolute;
width: 28px;
height: 28px;
flex-shrink: 0;
border-radius: 100px;
border: 4px solid var(--Min, #FFF);
left: ${({ left }) => left}px;
bottom: -14px;
background: var(--Neutral-Surface-Dark, #272727);
cursor: grab;
user-select: none;
&:active {
  cursor: grabbing;
}
`

const DesktopButtonsWrapper = styled.div`
  display: block;
  position: fixed;
  bottom: 15px;
  left: 16px;
  width: 640px;
  background: var(--Neutral-Surface-Light, #F6F6F6);
  border-radius: 0 0 16px 16px;
  z-index: 10000;
  box-sizing: border-box;

  @media (max-width: 1081px) {
    display: none;
  }
`





const FilterModal: React.FC<FilterModalProps> = ({ onClose, categories, validityFilters, onValidityFilterChange, selectedAdvancedFiltersCategories, onAdvancedFiltersCategoriesChange, onReset, coupons, onApplyFilters, onFilteredCountChange, onRegisterApplyCallback, currentSortOption, currentMinPrice, currentMaxPrice, sliderMax, availableTags, selectedTags, onTagsChange }) => {
    const [selectedSort, setSelectedSort] = useState<SortOption>(currentSortOption)
    const [minPrice, setMinPrice] = useState<number>(currentMinPrice)
    const [maxPrice, setMaxPrice] = useState<number>(currentMaxPrice)
    const [localSelectedTags, setLocalSelectedTags] = useState<string[]>(selectedTags)
    const [leftPosition, setLeftPosition] = useState<number>(0) // Left button position
    const [rightPosition, setRightPosition] = useState<number>(550) // Right button position
    const [isDragging, setIsDragging] = useState<'left' | 'right' | null>(null)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState<number>(576) // Dynamic width

    // Local state for filters (only applied on button click)
    const [localSelectedCategories, setLocalSelectedCategories] = useState<string[]>(selectedAdvancedFiltersCategories)
    const [localValidityFilters, setLocalValidityFilters] = useState(validityFilters)

    // Sync local state with props when modal opens
    React.useEffect(() => {
        setLocalSelectedCategories(selectedAdvancedFiltersCategories)
        setLocalValidityFilters(validityFilters)
        setLocalSelectedTags(selectedTags)
    }, [])

    // Measure container width dynamically for responsive behavior and set initial positions
    React.useEffect(() => {
        const updateContainerWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth
                setContainerWidth(width)

                // Set initial positions based on current prices (slider scale: 20..sliderMax)
                const priceSpan = sliderMax - 20
                const minPos = ((currentMinPrice - 20) / priceSpan) * width
                setLeftPosition(minPos)
                setRightPosition(((currentMaxPrice - 20) / priceSpan) * width)
            }
        }

        updateContainerWidth() // Initial call

        // Timeout to ensure layout is settled (sometimes needed for modal transitions)
        const timeoutId = setTimeout(updateContainerWidth, 100)

        window.addEventListener('resize', updateContainerWidth)
        return () => {
            window.removeEventListener('resize', updateContainerWidth)
            clearTimeout(timeoutId)
        }
    }, [currentMinPrice, currentMaxPrice, sliderMax])

    const handleMouseDown = (button: 'left' | 'right') => (e: React.MouseEvent) => {
        e.preventDefault()
        setIsDragging(button)
    }

    const handleTouchStart = (button: 'left' | 'right') => (e: React.TouchEvent) => {
        e.preventDefault()
        setIsDragging(button)
    }

    const handleMove = (clientX: number) => {
        if (!isDragging) return

        const container = document.querySelector('[data-price-container]')
        if (!container) return

        const rect = container.getBoundingClientRect()
        let newPosition = clientX - rect.left

        // Clamp position within bounds
        newPosition = Math.max(0, Math.min(containerWidth, newPosition))

        const priceSpan = sliderMax - 20

        if (isDragging === 'left') {
            // Left button can't go beyond right button
            if (newPosition < rightPosition) {
                setLeftPosition(newPosition)
                // Convert position to price
                // Formula: (Position / Width) * Range + Min
                const price = Math.round((newPosition / containerWidth) * priceSpan / 10) * 10 + 20
                setMinPrice(price)
            }
        } else if (isDragging === 'right') {
            // Right button can't go below left button
            if (newPosition > leftPosition) {
                setRightPosition(newPosition)
                // Convert position to price
                const price = Math.round((newPosition / containerWidth) * priceSpan / 10) * 10 + 20
                setMaxPrice(price)
            }
        }
    }

    const handleMouseMove = (e: MouseEvent) => {
        handleMove(e.clientX)
    }

    const handleTouchMove = (e: TouchEvent) => {
        if (e.touches.length > 0) {
            handleMove(e.touches[0].clientX)
        }
    }

    const handleMouseUp = () => {
        setIsDragging(null)
    }

    const handleTouchEnd = () => {
        setIsDragging(null)
    }

    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.addEventListener('touchmove', handleTouchMove)
            document.addEventListener('touchend', handleTouchEnd)
            return () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
                document.removeEventListener('touchmove', handleTouchMove)
                document.removeEventListener('touchend', handleTouchEnd)
            }
        }
    }, [isDragging, leftPosition, rightPosition])

    const handleResetAll = () => {
        // Reset local state
        setMinPrice(20)
        setMaxPrice(sliderMax)
        setLeftPosition(0)
        setRightPosition(containerWidth) // Right thumb at far end of slider
        setSelectedSort(SortOption.Newest)
        setLocalSelectedCategories(['הכל'])
        setLocalValidityFilters({
            lastChance: false,
            lastWeek: false,
            longExpiry: false,
        })
        setLocalSelectedTags([])
        // Also reset parent state
        onReset()
    }

    const handleApplyFilters = useCallback(() => {
        // Apply all filters to parent state
        onAdvancedFiltersCategoriesChange(localSelectedCategories)

        // Apply validity filters
        Object.keys(localValidityFilters).forEach((key) => {
            const filterKey = key as keyof typeof localValidityFilters
            if (localValidityFilters[filterKey] !== validityFilters[filterKey]) {
                onValidityFilterChange(filterKey)
            }
        })

        // Apply tags
        onTagsChange(localSelectedTags)

        // Apply price and sort
        onApplyFilters(selectedSort, minPrice, maxPrice)
        onClose()
    }, [localSelectedCategories, localValidityFilters, localSelectedTags, validityFilters, onAdvancedFiltersCategoriesChange, onValidityFilterChange, onTagsChange, onApplyFilters, selectedSort, minPrice, maxPrice, onClose])

    const handleLocalCategoryChange = (category: string) => {
        // If הכל is currently selected, replace it with the new category
        if (localSelectedCategories.includes('הכל')) {
            setLocalSelectedCategories([category])
        } else {
            // Otherwise, toggle the category
            if (localSelectedCategories.includes(category)) {
                const newCategories = localSelectedCategories.filter(cat => cat !== category)
                setLocalSelectedCategories(newCategories)
            } else {
                setLocalSelectedCategories([...localSelectedCategories, category])
            }
        }
    }

    const handleLocalTagToggle = (tag: string) => {
        setLocalSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        )
    }

    const isApplyDisabled = localSelectedCategories.length === 0

    const handleLocalValidityFilterChange = (filterName: keyof typeof localValidityFilters) => {
        setLocalValidityFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName],
        }))
    }

    // Calculate filtered coupons count based on current LOCAL filter selections
    const getFilteredCount = () => {
        return coupons.filter(coupon => {
            // Category filter (use local state). Empty array or "הכל" both mean "all".
            const isAllCategories = localSelectedCategories.length === 0 || localSelectedCategories.includes('הכל')
            const matchesCategory = isAllCategories || localSelectedCategories.includes(coupon.category)

            // Price filter
            const couponPrice = parseFloat(coupon.price)
            const matchesPrice = couponPrice >= minPrice && couponPrice <= maxPrice

            // Validity filter (use local state)
            let matchesValidity = true
            if (localValidityFilters.lastChance || localValidityFilters.lastWeek || localValidityFilters.longExpiry) {
                const now = new Date()
                const endAt = coupon.endAt ? new Date(coupon.endAt) : null

                if (!endAt) {
                    matchesValidity = false
                } else {
                    const hoursLeft = (endAt.getTime() - now.getTime()) / (1000 * 60 * 60)
                    const daysLeft = hoursLeft / 24

                    matchesValidity = false
                    // הזדמנות אחרונה - 24 hours or less left
                    if (localValidityFilters.lastChance && hoursLeft > 0 && hoursLeft <= 24) matchesValidity = true
                    // שבוע אחרון - less than 7 days (but more than 24 hours)
                    if (localValidityFilters.lastWeek && hoursLeft > 24 && daysLeft <= 7) matchesValidity = true
                    // חודש ויותר - more than 30 days left
                    if (localValidityFilters.longExpiry && daysLeft > 30) matchesValidity = true
                }
            }

            const matchesTags = localSelectedTags.length === 0 ||
                (coupon.tags ?? []).some(tag => localSelectedTags.includes(tag))

            return matchesCategory && matchesPrice && matchesValidity && matchesTags
        }).length
    }

    // Report filtered count to parent
    React.useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(getFilteredCount())
        }
    }, [localSelectedCategories, minPrice, maxPrice, localValidityFilters, localSelectedTags, coupons])

    // Register apply callback for mobile bottom nav
    React.useEffect(() => {
        if (onRegisterApplyCallback) {
            onRegisterApplyCallback(handleApplyFilters)
        }
    }, [handleApplyFilters, onRegisterApplyCallback])

    return (
        <Container>
            <ContentWrapper>
                <ModalHeader>
                    <Text style={{ fontSize: '24px' }} weight='bold' textcolor='#272727'>סינון הטבות</Text>
                    <div onClick={onClose} style={{ cursor: 'pointer' }}>
                        <XIcon />
                    </div>
                </ModalHeader>
                <ClearButtonContainer>
                    <ClearAllButton onClick={handleResetAll}>
                        <ReplayIcon />
                        <Text size='md' weight='medium' textcolor='#272727'>
                            איפוס
                        </Text>
                    </ClearAllButton>
                </ClearButtonContainer>
                <FiltersContainer>
                    <Text size='xxl' weight='bold' textcolor='#272727'>מיון לפי</Text>
                    <FlexLineWithGap onClick={() => setSelectedSort(SortOption.Newest)}>
                        <RadioButtonContainer>
                            <RadioButtonBase checked={selectedSort === SortOption.Newest} />
                        </RadioButtonContainer>
                        <Text size='md' weight='normal' textcolor='#272727'>החדשות ביותר</Text>
                    </FlexLineWithGap>
                    <FlexLineWithGap onClick={() => setSelectedSort(SortOption.PriceHighToLow)}>
                        <RadioButtonContainer>
                            <RadioButtonBase checked={selectedSort === SortOption.PriceHighToLow} />
                        </RadioButtonContainer>
                        <Text size='md' weight='normal' textcolor='#272727'>מהגבוה לנמוך</Text>
                    </FlexLineWithGap>
                    <FlexLineWithGap onClick={() => setSelectedSort(SortOption.PriceLowToHigh)}>
                        <RadioButtonContainer>
                            <RadioButtonBase checked={selectedSort === SortOption.PriceLowToHigh} />
                        </RadioButtonContainer>
                        <Text size='md' weight='normal' textcolor='#272727'>מהנמוך לגבוה</Text>
                    </FlexLineWithGap>
                </FiltersContainer>
                <FiltersContainer>
                    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '8px' }}>
                        <Text size='xxl' weight='bold' textcolor='#272727'>קטגוריות</Text>
                        <Text size='md' weight='normal' textcolor='#5D5D5D'>בחר קטגוריה אחת או יותר</Text>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {categories.map((category, index) => (
                            <CategoryChip
                                key={index}
                                className={localSelectedCategories.includes(category) ? 'active' : ''}
                                onClick={() => handleLocalCategoryChange(category)}
                            >
                                <Text size='md' weight='normal' textcolor={localSelectedCategories.includes(category) ? 'white' : '#272727'}>{category}</Text>
                            </CategoryChip>
                        ))}
                    </div>
                </FiltersContainer>
                {availableTags.length > 0 && (
                    <FiltersContainer>
                        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '8px' }}>
                            <Text size='xxl' weight='bold' textcolor='#272727'>תגיות</Text>
                            <Text size='md' weight='normal' textcolor='#5D5D5D'>בחר תגית אחת או יותר</Text>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {availableTags.map((tag, index) => (
                                <CategoryChip
                                    key={index}
                                    className={localSelectedTags.includes(tag) ? 'active' : ''}
                                    onClick={() => handleLocalTagToggle(tag)}
                                >
                                    <Text size='md' weight='normal' textcolor={localSelectedTags.includes(tag) ? 'white' : '#272727'}>{tag}</Text>
                                </CategoryChip>
                            ))}
                        </div>
                    </FiltersContainer>
                )}
                <FiltersContainer>
                    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '8px' }}>
                        <Text size='xxl' weight='bold' textcolor='#272727'>תוקף</Text>
                    </div>
                    <FlexLineWithGap onClick={() => handleLocalValidityFilterChange('lastChance')} style={{ cursor: 'pointer' }}>
                        <CheckBox type='checkbox' checked={localValidityFilters.lastChance} readOnly />
                        <Text size='md' weight='normal' textcolor='#272727'>הזדמנות אחרונה (יפוג בעוד שעות בודדות)</Text>
                    </FlexLineWithGap>
                    <FlexLineWithGap onClick={() => handleLocalValidityFilterChange('lastWeek')} style={{ cursor: 'pointer' }}>
                        <CheckBox type='checkbox' checked={localValidityFilters.lastWeek} readOnly />
                        <Text size='md' weight='normal' textcolor='#272727'>שבוע אחרון להטבה</Text>
                    </FlexLineWithGap>
                    <FlexLineWithGap onClick={() => handleLocalValidityFilterChange('longExpiry')} style={{ cursor: 'pointer' }}>
                        <CheckBox type='checkbox' checked={localValidityFilters.longExpiry} readOnly />
                        <Text size='md' weight='normal' textcolor='#272727'>חודש ויותר להטבה</Text>
                    </FlexLineWithGap>
                </FiltersContainer>
                <FiltersContainer marginBottom={100}>
                    <Text size='xxl' weight='bold' textcolor='#272727'>טווח מחירים</Text>
                    <CandleStickContainer ref={containerRef} data-price-container>
                        <CandleStick height={0} />
                        <CandleStick height={0} />
                        <CandleStick height={0} />
                        <CandleStick height={0} />
                        <CandleStick height={3} />
                        <CandleStick height={3} />
                        <CandleStick height={3} />
                        <CandleStick height={22.225} />
                        <CandleStick height={22.225} />
                        <CandleStick height={31.486} />
                        <CandleStick height={31.486} />
                        <CandleStick height={31.486} />
                        <CandleStick height={31.486} />
                        <CandleStick height={31.486} />
                        <CandleStick height={42.598} />
                        <CandleStick height={46.302} />
                        <CandleStick height={50.006} />
                        <CandleStick height={53} />
                        <CandleStick height={57} />
                        <CandleStick height={60} />
                        <CandleStick height={70} />
                        <CandleStick height={80} />
                        <CandleStick height={80} />
                        <CandleStick height={75} />
                        <CandleStick height={70} />
                        <CandleStick height={65} />
                        <CandleStick height={60} />
                        <CandleStick height={55} />
                        <CandleStick height={50} />
                        <CandleStick height={45} />
                        <CandleStick height={40} />
                        <CandleStick height={35} />
                        <CandleStick height={30} />
                        <CandleStick height={25} />
                        <CandleStick height={20} />
                        <CandleStick height={15} />
                        <CandleStick height={10} />
                        <CandleStick height={35} />
                        <CandleStick height={30} />
                        <CandleStick height={25} />
                        <CandleStick height={20} />
                        <CandleStick height={15} />
                        <CandleStick height={10} />
                        <CandleStick height={3} />
                        <CandleStick height={3} />
                        <CandleStick height={3} />
                        <CandleStick height={3} />
                        <CandleStick height={3} />
                        <CandleStick height={0} />
                        <CandleStick height={0} />
                        <CandleStick height={0} />
                        <CandleStick height={0} />
                        <BlackDragButton
                            left={rightPosition}
                            onMouseDown={handleMouseDown('right')}
                            onTouchStart={handleTouchStart('right')}
                            style={{ cursor: isDragging === 'right' ? 'grabbing' : 'grab' }}
                        />
                        <BlackDragButton
                            left={leftPosition}
                            onMouseDown={handleMouseDown('left')}
                            onTouchStart={handleTouchStart('left')}
                            style={{ cursor: isDragging === 'left' ? 'grabbing' : 'grab' }}
                        />
                    </CandleStickContainer>
                    <PriceDisplayButton>
                        <DisplayButton>
                            <Text size='md' weight='normal' textcolor='#272727'> {maxPrice} ש"ח</Text>
                        </DisplayButton>
                        <DisplayButton>
                            <Text size='md' weight='normal' textcolor='#272727'> {minPrice} ש"ח</Text>
                        </DisplayButton>
                    </PriceDisplayButton>
                </FiltersContainer>
            </ContentWrapper>
            <DesktopButtonsWrapper>
                <ModalButtonContainer>
                    <Button variant='contained' onClick={handleApplyFilters} disabled={isApplyDisabled} style={{ backgroundColor: isApplyDisabled ? '#B0B0B0' : '#007EFF', boxShadow: 'none' }}>
                        <Text size='xxl' weight='bold' textcolor='white'> צפייה בהטבות ({getFilteredCount()})</Text>
                    </Button>
                    <Button onClick={onClose} variant='contained' style={{ backgroundColor: '#FFF', boxShadow: 'none', borderRadius: '8px', border: '1px solid var(--Neutral-Borders-Mid, #B0B0B0)', background: 'var(--Neutral-Surface-Minimal, #FFF)' }}>
                        <Text size='xxl' weight='bold' textcolor='#272727'>ביטול</Text>
                    </Button>
                </ModalButtonContainer>
            </DesktopButtonsWrapper>
        </Container>
    )
}

export default FilterModal